import { Tooltip } from '@protoss/ui';
import './confidence-badge.css';

export type ConfidenceLevel = 'high' | 'medium' | 'low';

export interface ConfidenceBadgeProps {
  /** The already-computed confidence carried by the payload (never inferred client-side). */
  level: ConfidenceLevel;
  /** Optional tooltip explaining what the confidence reflects. Omitted = plain chip. */
  tooltip?: string;
}

const LABELS: Record<ConfidenceLevel, string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
};

/**
 * Small read-only chip surfacing an AI output's confidence level ('high' | 'medium' | 'low').
 * Purely presentational: callers render it only when the payload actually carries a confidence
 * value - an absent confidence means no badge, never a client-side guess.
 */
export function ConfidenceBadge({ level, tooltip }: ConfidenceBadgeProps) {
  const label = LABELS[level];
  const chip = (
    <span
      className={`confidence-badge confidence-badge--${level}`}
      role="status"
      aria-label={label}
    >
      <span className="confidence-badge__dot" aria-hidden="true" />
      <span className="confidence-badge__label">{label}</span>
    </span>
  );
  if (!tooltip) return chip;
  return <Tooltip.Simple label={tooltip}>{chip}</Tooltip.Simple>;
}
